import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Activity, DollarSign, Users, TrendingUp, Zap, Loader2, ChevronRight,
  Building2, UserRound, FileDown, BookOpen,
} from "lucide-react";

/**
 * Superadmin — AI Usage & Costs.
 *
 * Rolls up every billable event (LLM tokens, Veryfi OCR, Resend emails,
 * Plaid linked items) into a single spend dashboard. The mockup this
 * matches (rocketsuite reference) sets the visual grammar:
 *
 *   [ range chips ]
 *   [ category chips ]
 *   [ 4 KPI cards ]
 *   [ By Feature ]   [ All Cost Categories ]
 *
 * Costs are stored in cents (float) on the backend to survive fractional
 * per-token pricing; formatting is done once here at the boundary so the
 * ledger stays precise.
 */
const RANGES = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "month", label: "This month" },
];

const CATEGORIES = [
  { key: "all", label: "All categories" },
  { key: "llm", label: "llm" },
  { key: "bank", label: "bank" },
  { key: "email", label: "email" },
  { key: "ocr", label: "ocr" },
  { key: "infra", label: "infra" },
];

const money = (cents) => {
  const dollars = (Number(cents) || 0) / 100;
  if (dollars >= 100) return `$${dollars.toFixed(2)}`;
  if (dollars >= 0.01) return `$${dollars.toFixed(2)}`;
  if (dollars > 0) return `$${dollars.toFixed(6).replace(/0+$/, "").replace(/\.$/, "")}`;
  return "$0.00";
};
const compact = (n) => Number(n || 0).toLocaleString();

const SERVICE_LABEL = {
  openai_llm: "OpenAI — LLM tokens",
  anthropic_llm: "Anthropic — LLM tokens",
  veryfi_ocr: "Veryfi OCR",
  resend_email: "Resend email",
  plaid_linked_item: "Plaid linked items",
  emergent_compute: "Emergent compute (K8s)",
  mongodb_storage: "MongoDB Atlas storage",
  emergent_object_storage: "Emergent object storage",
};

export default function SuperadminUsage({
  // Override these to reuse this component for the partner-scoped
  // view — same UI, different endpoint / breadcrumb / title.
  endpoint = "/admin/usage",
  title = "Usage & Costs",
  breadcrumb = "SuperAdmin · Usage & Costs",
  testId = "usage-page",
} = {}) {
  const [range, setRange] = useState("month");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const sep = endpoint.includes("?") ? "&" : "?";
    api.get(`${endpoint}${sep}range=${range}${category !== "all" ? `&category=${category}` : ""}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setData({}); setLoading(false); });
  }, [range, category, endpoint]);

  const totals = data?.totals || {};
  const byFeature = data?.by_feature || [];
  const byService = data?.by_service || [];
  const byCategory = data?.by_category || [];
  const byCompany = data?.by_company || [];
  const byUser = data?.by_user || [];
  const expected = data?.expected_services || [];
  const catSources = data?.categorization_sources_overall || null;

  // Category chip totals — sum from byCategory response (unfiltered).
  const catTotals = useMemo(() => {
    const m = {};
    for (const r of byCategory) m[r.category] = r.cost_cents;
    return m;
  }, [byCategory]);

  // Merge expected + actual services so unused integrations still appear.
  const mergedServices = useMemo(() => {
    const byKey = new Map();
    for (const s of expected) byKey.set(s.service, { ...s, cost_cents: 0, quantity: 0, unit: s.unit, events: 0 });
    for (const s of byService) byKey.set(s.service, { ...(byKey.get(s.service) || {}), ...s });
    return Array.from(byKey.values());
  }, [expected, byService]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
        <Activity size={14} /> {breadcrumb}
      </div>
      <h1 className="text-2xl font-heading font-bold text-slate-900 mb-4">
        {title}
      </h1>

      {/* Downloadable go-to-market playbook (superadmin-only). */}
      <GtmDocsPanel />

      {/* Date range chips */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {RANGES.map(r => (
          <button
            key={r.key}
            data-testid={`usage-range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1.5 rounded-md border text-sm transition ${
              range === r.key
                ? "bg-white border-cyan-300 text-cyan-800 ring-1 ring-cyan-200"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {CATEGORIES.map(c => {
          const active = category === c.key;
          const chipCost = c.key === "all" ? totals.cost_cents : catTotals[c.key];
          return (
            <button
              key={c.key}
              data-testid={`usage-cat-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                active
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c.label}
              {chipCost !== undefined && (
                <span className={`ml-1.5 tabular-nums ${active ? "text-slate-300" : "text-slate-400"}`}>
                  · {money(chipCost || 0)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Total cost" value={money(totals.cost_cents)} icon={DollarSign} accent="emerald" />
        <KpiCard label="Total events" value={compact(totals.events)} icon={Zap} accent="cyan" />
        <KpiCard label="Unique users" value={compact(totals.unique_users)} icon={Users} accent="indigo" />
        <KpiCard
          label="Avg cost / event"
          value={
            totals.events && totals.events > 0
              ? `$${(totals.cost_cents / totals.events / 100).toFixed(6).replace(/0+$/, "").replace(/\.$/, "") || "0"}`
              : "$0.00"
          }
          icon={TrendingUp} accent="amber"
        />
      </div>

      {/* Categorization source breakdown — proves the deterministic
          layers (Plaid PFC + cross-tenant merchant cache + rules) are
          doing the heavy lifting so LLM cost stays near zero. */}
      {catSources && <CategorizationSourcesCard sources={catSources} />}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* By Feature — per-feature model breakdown so ops can spot
             which Sonnet routes could safely move to Haiku. Feb 2026. */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="usage-by-feature">
          <div className="px-4 py-3 border-b border-slate-100 font-medium text-slate-700 text-sm flex items-center justify-between">
            <span>By Feature × Model <span className="text-slate-400 font-normal">({rangeLabel(range)})</span></span>
            {loading && <Loader2 size={13} className="animate-spin text-slate-400" />}
          </div>
          {byFeature.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {loading ? "Loading…" : "No AI events in this range yet."}
            </div>
          ) : (
            <div className="max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Feature</th>
                    <th className="text-left px-4 py-2 font-medium">Model</th>
                    <th className="text-right px-4 py-2 font-medium">Calls</th>
                    <th className="text-right px-4 py-2 font-medium">$/call</th>
                    <th className="text-right px-4 py-2 font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byFeature.map((f, i) => (
                    <FeatureRow key={`${f.feature}-${f.model}-${i}`} row={f} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* All Cost Categories (services) */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="usage-by-service">
          <div className="px-4 py-3 border-b border-slate-100 font-medium text-slate-700 text-sm">
            All Cost Categories <span className="text-slate-400 font-normal">({rangeLabel(range)})</span>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Service</th>
                  <th className="text-right px-4 py-2 font-medium">Quantity</th>
                  <th className="text-right px-4 py-2 font-medium">Rate</th>
                  <th className="text-right px-4 py-2 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mergedServices.map(s => (
                  <ServiceRow key={s.service} row={s} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Per Enterprise (Company) + Per User */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="usage-by-company">
          <div className="px-4 py-3 border-b border-slate-100 font-medium text-slate-700 text-sm flex items-center gap-2">
            <Building2 size={14} className="text-slate-400" />
            By Enterprise
            <span className="text-slate-400 font-normal ml-1">({rangeLabel(range)})</span>
          </div>
          {byCompany.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {loading ? "Loading…" : "No enterprise events in this range yet."}
            </div>
          ) : (
            <div className="max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Enterprise</th>
                    <th className="text-right px-4 py-2 font-medium">Users</th>
                    <th className="text-right px-4 py-2 font-medium">Cost</th>
                    <th className="text-right px-4 py-2 font-medium">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byCompany.map(c => {
                    const src = c.categorization_sources || {};
                    const srcTotal = (src.pfc || 0) + (src.cache || 0) + (src.rule || 0) + (src.ai || 0) + (src.other || 0);
                    return (
                    <tr key={c.company_id} data-testid={`company-row-${c.company_id}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-slate-900 text-sm">{c.name || "—"}</div>
                        {(c.plaid_items > 0 || c.infra_total_cents > 0) && (
                          <div className="text-[11px] text-slate-500">
                            AI ${(c.cost_cents / 100).toFixed(4).replace(/0+$/, "").replace(/\.$/, "") || "0"}
                            {c.plaid_items > 0 && <> · Plaid ${(c.plaid_cost_cents / 100).toFixed(2)}</>}
                            {c.infra_total_cents > 0 && (
                              <> · <span className="text-amber-700"
                                        title={`Modeled infra: compute $${(c.infra_compute_cents/100).toFixed(4)} + Mongo $${(c.infra_mongodb_cents/100).toFixed(4)} + storage $${(c.infra_storage_cents/100).toFixed(4)}`}>
                                Infra ${(c.infra_total_cents / 100).toFixed(2)}
                              </span></>
                            )}
                          </div>
                        )}
                        {c.billing_product && (
                          <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">
                            {c.billing_product.replace(/_/g, " ")} · ${(c.subscription_cents / 100).toFixed(2)}/period
                          </div>
                        )}
                        {srcTotal > 0 && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="flex h-1.5 flex-1 max-w-[180px] rounded-full overflow-hidden border border-slate-100">
                              {SOURCE_META.map(m => {
                                const n = src[m.key] || 0;
                                if (n === 0) return null;
                                const pct = (n / srcTotal) * 100;
                                return <div key={m.key} className={m.tone} style={{ width: `${pct}%` }} title={`${m.label}: ${n}`} />;
                              })}
                            </div>
                            <span className="text-[10px] text-slate-400 tabular-nums">
                              {src.ai > 0 ? `${((src.ai / srcTotal) * 100).toFixed(1)}% AI` : "0% AI"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-600 tabular-nums text-xs">{compact(c.unique_users || 0)}</td>
                      <td className="px-4 py-2 text-right font-medium text-slate-900 tabular-nums">
                        {money(c.total_cost_cents)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        <MarginCell company={c} />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="usage-by-user">
          <div className="px-4 py-3 border-b border-slate-100 font-medium text-slate-700 text-sm flex items-center gap-2">
            <UserRound size={14} className="text-slate-400" />
            By User
            <span className="text-slate-400 font-normal ml-1">({rangeLabel(range)})</span>
          </div>
          {byUser.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {loading ? "Loading…" : "No user-attributed events in this range yet."}
            </div>
          ) : (
            <div className="max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">User</th>
                    <th className="text-left px-4 py-2 font-medium">Role</th>
                    <th className="text-right px-4 py-2 font-medium">Events</th>
                    <th className="text-right px-4 py-2 font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byUser.map(u => (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-slate-900 text-sm">{u.name || "—"}</div>
                        <div className="text-[11px] text-slate-500">{u.email || u.user_id}</div>
                      </td>
                      <td className="px-4 py-2">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-4 py-2 text-right text-slate-600 tabular-nums text-xs">{compact(u.events)}</td>
                      <td className="px-4 py-2 text-right font-medium text-slate-900 tabular-nums">
                        {money(u.cost_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
function KpiCard({ label, value, icon: Icon, accent }) {
  const tone = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    cyan: "text-cyan-700 bg-cyan-50 border-cyan-100",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
  }[accent];
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-heading font-bold text-slate-900 tabular-nums">{value}</div>
        {Icon && (
          <div className={`ml-auto w-8 h-8 rounded-md flex items-center justify-center border ${tone}`}>
            <Icon size={14} />
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceRow({ row }) {
  const label = SERVICE_LABEL[row.service] || row.service;
  const cost = row.cost_cents || 0;
  const dim = cost === 0;
  const quantity = row.quantity ?? 0;
  const rate = row.unit_price_usd;

  // For LLM (per-token pricing), show a per-1M rate for readability
  // based on the model actually recorded on the row.
  const LLM_RATES_PER_1M = {
    "gpt-4o-mini": 0.15,
    "gpt-4o": 2.50,
    "gpt-4.1-mini": 0.40,
    "gpt-4.1": 2.00,
    "gpt-5": 2.50,
    "gpt-5-mini": 0.25,
    "claude-sonnet-4-5-20250929": 3.00,
    "claude-haiku-4-5-20251001": 1.00,
  };
  const rateDisplay = row.service.endsWith("_llm")
    ? (row.model && LLM_RATES_PER_1M[row.model] !== undefined
        ? `$${LLM_RATES_PER_1M[row.model].toFixed(2)} / 1M`
        : "—")
    : (rate !== undefined && rate !== null
        ? `$${Number(rate).toFixed(rate < 0.01 ? 6 : 4)} / ${row.unit || "unit"}`
        : "—");

  const qtyDisplay = quantity > 0
    ? `${compact(quantity)} ${row.unit || (row.service.endsWith("_llm") ? "tokens" : "")}`
    : "—";

  return (
    <tr data-testid={`service-row-${row.service}`} className={dim ? "opacity-50" : ""}>
      <td className="px-4 py-2">
        <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5">
          {label}
          {row.estimated && (
            <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                  title="Modeled from your platform monthly bill, apportioned by this book's activity share. Not billed per event.">
              Estimated
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-400">
          / {row.unit || "unit"}
        </div>
      </td>
      <td className="px-4 py-2 text-right text-slate-600 tabular-nums text-xs">{qtyDisplay}</td>
      <td className="px-4 py-2 text-right text-slate-500 tabular-nums text-xs">{rateDisplay}</td>
      <td className="px-4 py-2 text-right font-medium text-slate-900 tabular-nums">
        {money(cost)}
      </td>
    </tr>
  );
}

function rangeLabel(key) {
  return RANGES.find(r => r.key === key)?.label || key;
}

/* Deterministic-vs-AI source palette. Colors chosen to match the KPI accents
   already in use — no new palette introduced. */
const SOURCE_META = [
  { key: "pfc",   label: "Plaid PFC",    tone: "bg-cyan-500",    tip: "Categorized deterministically from Plaid's Personal Finance Category tag — no AI, no rules needed." },
  { key: "cache", label: "Merchant cache", tone: "bg-emerald-500", tip: "Cross-tenant merchant cache hit — a merchant your platform has categorized before for any client." },
  { key: "rule",  label: "Company rule", tone: "bg-indigo-500",  tip: "Company-specific rule matched (created after ≥2 human approvals of the same merchant → account)." },
  { key: "ai",    label: "LLM",          tone: "bg-amber-500",   tip: "Fell through the deterministic layers → billable OpenAI call." },
  { key: "other", label: "Manual / other", tone: "bg-slate-400", tip: "Manually entered, opening balance, or uncategorized." },
];

function CategorizationSourcesCard({ sources }) {
  const total = (sources.pfc || 0) + (sources.cache || 0) + (sources.rule || 0) + (sources.ai || 0) + (sources.other || 0);
  if (total === 0) return null;
  const aiPct = (sources.ai / total) * 100;
  const deterministicPct = ((sources.pfc + sources.cache + sources.rule) / total) * 100;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4" data-testid="usage-cat-sources">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Categorization source</div>
          <div className="text-sm text-slate-500 mt-0.5">
            <span className="font-semibold text-slate-900 tabular-nums">{compact(total)}</span> categorized transactions across the platform
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading font-bold text-emerald-700 tabular-nums">{deterministicPct.toFixed(1)}%</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Zero-AI cost path</div>
        </div>
      </div>
      <StackedBar sources={sources} total={total} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        {SOURCE_META.map(m => {
          const n = sources[m.key] || 0;
          const pct = total > 0 ? (n / total) * 100 : 0;
          return (
            <div key={m.key} title={m.tip} className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-sm ${m.tone}`} />
              <div className="min-w-0">
                <div className="text-[11px] text-slate-500 truncate">{m.label}</div>
                <div className="text-sm font-medium text-slate-900 tabular-nums">
                  {compact(n)} <span className="text-slate-400 text-xs">({pct.toFixed(1)}%)</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {aiPct > 0 && (
        <div className="mt-3 text-[11px] text-slate-500">
          LLM ran on <span className="font-semibold text-amber-700">{aiPct.toFixed(2)}%</span> of transactions — the rest bypassed AI via deterministic layers.
        </div>
      )}
    </div>
  );
}


/**
 * MarginCell — subscription list price minus total hard cost, coloured

/**
 * FeatureRow — one row of the "By Feature × Model" table. Colour-
 * codes the model chip so ops can spot expensive routes (Sonnet) at
 * a glance and add a "consider Haiku?" hint on features that never
 * touch dollars or client-facing text.
 *
 * Tiering:
 *   • cheap    — gpt-4o-mini, claude-haiku-4-*, gpt-5-mini
 *   • balanced — gpt-4o, gpt-4.1, gpt-5
 *   • premium  — claude-sonnet-*, claude-opus-*, gpt-4-turbo
 */
function FeatureRow({ row }) {
  const model = row.model || "—";
  const isPremium = /sonnet|opus|gpt-4o$|gpt-4-turbo|gpt-4$/i.test(model);
  const isCheap = /mini|haiku/i.test(model);
  const chipTone = isPremium
    ? { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", label: "premium" }
    : isCheap
      ? { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", label: "cheap" }
      : { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", label: "balanced" };
  const perCall = row.events > 0 ? row.cost_cents / row.events : 0;

  // Features that touch dollars-at-stake / client-facing text —
  // downgrading these to Haiku is risky. Everything else is fair game.
  const dollarCritical = /proposal|invoice|estimate|payment|reconcile|cpa/i.test(row.feature);
  const showDowngradeHint = isPremium && !dollarCritical && perCall > 1;  // > 1¢/call

  return (
    <tr data-testid={`feature-row-${row.feature}-${row.model}`}>
      <td className="px-4 py-2 font-mono text-xs text-slate-700 align-top">
        {row.feature}
        {showDowngradeHint && (
          <div className="text-[10px] text-amber-700 mt-0.5"
               title="Not dollar-critical. Consider moving to Haiku 4.5 or gpt-4o-mini — could cut 60-80% off this row.">
            ↓ Haiku candidate
          </div>
        )}
      </td>
      <td className="px-4 py-2 align-top">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono ${chipTone.bg} ${chipTone.text} ${chipTone.border}`}
               title={`${row.provider} · ${chipTone.label}`}>
          {model}
        </span>
      </td>
      <td className="px-4 py-2 text-right text-slate-600 tabular-nums align-top">{compact(row.events)}</td>
      <td className="px-4 py-2 text-right text-slate-500 tabular-nums text-xs align-top">
        {perCall > 0 ? (perCall < 0.5 ? `$${(perCall/100).toFixed(6).replace(/0+$/, "").replace(/\.$/, "")}` : `$${(perCall/100).toFixed(4)}`) : "$0"}
      </td>
      <td className="px-4 py-2 text-right font-medium text-slate-900 tabular-nums align-top">
        {money(row.cost_cents)}
      </td>
    </tr>
  );
}

/**
 * MarginCell — subscription list price minus total hard cost, coloured
 * by health tier. Rendered inside the enterprise table so pros can
 * spot loss-leader books at a glance.
 *   • rose  (loss)     margin < 0 — costs more than they pay
 *   • slate (unpaid)   no billing_product set / comp'd
 *   • amber (thin)     paying < 2× their cost
 *   • emerald (healthy) 2-5× their cost
 *   • teal (fat)       5×+ their cost — perfect upsell candidates? No, keep them.
 */
function MarginCell({ company }) {
  const sub = Number(company.subscription_cents || 0);
  const margin = Number(company.margin_cents || 0);
  const tier = company.margin_tier;
  const ratio = Number(company.margin_ratio || 0);
  const tone = {
    loss:    { bg: "bg-rose-50",    text: "text-rose-700",    dot: "bg-rose-500",    label: "Loss" },
    unpaid:  { bg: "bg-slate-50",   text: "text-slate-500",   dot: "bg-slate-400",   label: "Unpaid" },
    thin:    { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500",   label: "Thin" },
    healthy: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "Healthy" },
    fat:     { bg: "bg-teal-50",    text: "text-teal-700",    dot: "bg-teal-500",    label: "Strong" },
  }[tier] || { bg: "bg-slate-50", text: "text-slate-400", dot: "bg-slate-300", label: "—" };
  const sign = margin < 0 ? "-" : "";
  const magnitude = Math.abs(margin);
  const tip = sub > 0
    ? `Subscription $${(sub/100).toFixed(2)} − hard cost $${((sub - margin)/100).toFixed(2)} = ${sign}$${(magnitude/100).toFixed(2)} (${ratio > 0 ? ratio.toFixed(1) + "×" : "no revenue"})`
    : "No billing_product set on this company — treats as $0 revenue.";
  return (
    <div className={`inline-flex flex-col items-end gap-0.5 px-2 py-1 rounded-md ${tone.bg}`} title={tip}>
      <div className={`inline-flex items-center gap-1 text-xs font-medium ${tone.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
        {sub > 0 ? `${sign}$${(magnitude/100).toFixed(2)}` : "—"}
      </div>
      <div className={`text-[9px] uppercase tracking-wide ${tone.text} opacity-80`}>
        {tone.label}{sub > 0 && ratio > 0 ? ` · ${ratio.toFixed(1)}×` : ""}
      </div>
    </div>
  );
}

function StackedBar({ sources, total }) {
  return (
    <div className="flex h-3 w-full rounded-full overflow-hidden border border-slate-100">
      {SOURCE_META.map(m => {
        const n = sources[m.key] || 0;
        if (n === 0) return null;
        const pct = (n / total) * 100;
        return (
          <div
            key={m.key}
            className={m.tone}
            style={{ width: `${pct}%` }}
            title={`${m.label}: ${compact(n)} (${pct.toFixed(1)}%)`}
          />
        );
      })}
    </div>
  );
}

function RoleBadge({ role }) {
  if (!role) return <span className="text-xs text-slate-400">—</span>;
  const tone = {
    superadmin: "bg-indigo-50 text-indigo-700 border-indigo-100",
    pro: "bg-cyan-50 text-cyan-700 border-cyan-100",
    client: "bg-emerald-50 text-emerald-700 border-emerald-100",
  }[role] || "bg-slate-50 text-slate-600 border-slate-100";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide ${tone}`}>
      {role}
    </span>
  );
}


/**
 * GtmDocsPanel — collapsible superadmin-only download panel for the
 * CypherPro GTM playbook (service outline + three pitch decks). Files
 * live in /app/memory/ and are served straight from disk by the
 * /api/admin/gtm-docs endpoint.
 *
 * Each row is a real browser download (Content-Disposition: attachment)
 * with the current file's byte size + last-modified timestamp so ops
 * can spot stale docs. Mar 2026.
 */
function GtmDocsPanel() {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!open || docs.length) return;
    setLoading(true);
    api.get("/admin/gtm-docs")
      .then(r => setDocs(r.data?.docs || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [open, docs.length]);

  const download = async (doc, fmt = "md") => {
    setDownloading(`${doc.slug}-${fmt}`);
    try {
      const resp = await api.get(
        `/admin/gtm-docs/${doc.slug}${fmt === "pdf" ? "?fmt=pdf" : ""}`,
        { responseType: "blob" },
      );
      const mime = fmt === "pdf" ? "application/pdf" : "text/markdown";
      const blob = new Blob([resp.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf"
        ? doc.filename.replace(/\.md$/i, ".pdf")
        : doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setDownloading(null);
    }
  };

  const humanBytes = (n) => {
    if (n > 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    if (n > 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${n} B`;
  };
  const relTime = (unix) => {
    if (!unix) return "—";
    const d = new Date(unix * 1000);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="mb-4 rounded-lg border border-indigo-100 bg-gradient-to-br from-indigo-50/60 to-white overflow-hidden"
         data-testid="gtm-docs-panel">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-indigo-50/40 transition"
        data-testid="gtm-docs-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-indigo-600" />
          <span className="font-medium text-slate-800 text-sm">Go-to-Market Playbook</span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
            {docs.length ? `${docs.length} docs` : "downloadable"}
          </span>
        </div>
        <ChevronRight size={14} className={`text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-indigo-100 divide-y divide-slate-100 bg-white">
          {loading && (
            <div className="p-4 text-center text-slate-400 text-sm">
              <Loader2 size={14} className="inline animate-spin mr-2" /> Loading catalog…
            </div>
          )}
          {!loading && docs.map(d => (
            <div key={d.slug} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800 text-sm">{d.label}</span>
                  {!d.available && (
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                      Missing
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{d.description}</div>
                {d.available && (
                  <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                    {d.filename} · {humanBytes(d.size_bytes)} · Updated {relTime(d.modified_at)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => download(d, "pdf")}
                  disabled={!d.available || downloading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-40"
                  data-testid={`gtm-doc-download-pdf-${d.slug}`}
                  title="Download as branded PDF"
                >
                  {downloading === `${d.slug}-pdf` ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                  PDF
                </button>
                <button
                  onClick={() => download(d, "md")}
                  disabled={!d.available || downloading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium disabled:opacity-40 border border-slate-200"
                  data-testid={`gtm-doc-download-md-${d.slug}`}
                  title="Download as raw markdown"
                >
                  {downloading === `${d.slug}-md` ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                  MD
                </button>
              </div>
            </div>
          ))}
          {!loading && !docs.length && (
            <div className="p-4 text-center text-slate-400 text-sm">No documents available.</div>
          )}
        </div>
      )}
    </div>
  );
}
